import type { ClassSummary, StudentRow } from '../../../shared/api/classes'

/**
 * E7's decisions, out of the component.
 *
 * 🔴 THE REPO HAS NO DOM RENDERER (OD-60), so a branch made inside a `.tsx` is a
 * branch no test can reach — and the branches here decide which children a head
 * teacher is shown and which ones a search hides. `teachersModel.ts` and
 * `schoolDashboardModel.ts` are the precedent for every screen in this tier.
 *
 * 🔴 THE ORDER IS THE SERVER'S AND THIS FILE DOES NOT RE-SORT. `GET /students`
 * returns grade → section → roll already, and a second ordering rule in the
 * browser is a second answer to *"which child is first"* that would disagree
 * with the class tier the day either changed. Everything below FILTERS and
 * GROUPS; the sequence within a group is the one that arrived.
 */

/* ────────────────────────────────────────────────── one child's activity ── */

/**
 * 🔴 THIS IS NOT E1's "PLAYED THIS WEEK", AND THE DIFFERENCE IS THE SOURCE.
 *
 * The dashboard tile counts SESSIONS started in a rolling seven days, folded by
 * the server (`school-metrics.ts`). This reads `Progress.lastPlayed`, which is
 * what the child's own device last wrote. The two are usually the same number
 * and are not the same fact: a session that ran under `recorded: false` (I5)
 * makes a session row and writes nothing to progress.
 *
 * ⚠️ SO THIS NEVER BECOMES A HEADLINE ON THE SCREEN. `design-system.md` §8 —
 * *"sibling panels cannot contradict"* — and a school-wide "played this week"
 * computed here could differ by a child from the tile one nav item away. It is
 * a per-row fact and a filter, both of which are true of the row they describe.
 */
export type ActivityState = 'NEVER' | 'RECENT' | 'EARLIER'

const WEEK_MS = 7 * 86_400_000

export function activityState(row: StudentRow, now: Date): ActivityState {
  if (row.lastPlayed === null) return 'NEVER'
  const at = new Date(row.lastPlayed).getTime()
  // An unparseable date is not evidence that a child played. Never, not recent.
  if (!Number.isFinite(at)) return 'NEVER'
  return now.getTime() - at <= WEEK_MS ? 'RECENT' : 'EARLIER'
}

/* ─────────────────────────────────────────────────────── what is shown ── */

export type ActivityFilter = 'ALL' | 'RECENT' | 'QUIET' | 'NEVER'

export const ACTIVITY_FILTERS: readonly ActivityFilter[] = ['ALL', 'RECENT', 'QUIET', 'NEVER']

export interface StudentFilter {
  /** Free text over name, roll, class and parent name. Trimmed and case-folded. */
  query?: string
  /** One class, or every class. */
  classId?: string | null
  activity?: ActivityFilter
}

/**
 * ⚠️ THE PARENT'S PHONE IS NOT SEARCHABLE, AND THAT IS DELIBERATE RATHER THAN AN
 * OVERSIGHT. It arrives masked (`TeachingService.allStudents` masks it before it
 * leaves the server), so matching against it would answer for the digits a
 * school can see and refuse the ones they typed from a register — a search box
 * that works for some numbers and not others teaches an admin the screen is
 * unreliable. Name, roll, class and parent name are all whole values.
 */
export function matchesQuery(row: StudentRow, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (q === '') return true
  return [row.name, row.rollNumber, row.className, row.parentName].some(
    (field) => field !== null && field.toLowerCase().includes(q),
  )
}

export function filterStudents(
  rows: readonly StudentRow[],
  filter: StudentFilter,
  now: Date,
): StudentRow[] {
  const { query = '', classId = null, activity = 'ALL' } = filter
  return rows.filter((row) => {
    if (classId !== null && row.classId !== classId) return false
    if (!matchesQuery(row, query)) return false
    if (activity === 'ALL') return true
    const state = activityState(row, now)
    if (activity === 'RECENT') return state === 'RECENT'
    if (activity === 'NEVER') return state === 'NEVER'
    /* QUIET is "has played, but not lately" — a different child from one who has
       never started, and a different conversation with their teacher. */
    return state === 'EARLIER'
  })
}

/* ───────────────────────────────────────────────────────────── grouping ── */

export interface StudentGroup {
  classId: string
  heading: string
  rows: StudentRow[]
}

/**
 * The rows a class at a time, in the order the classes first appear.
 *
 * 🔴 FIRST-APPEARANCE ORDER IS THE SERVER'S ORDER. The rows arrive sorted by
 * grade, then section, then roll, so walking them once and opening a group when
 * a new class id turns up reproduces the timetable without deciding anything.
 * Sorting the groups here by name would put "Nursery B" above "Nursery A" the
 * first time a grade label stops being alphabetical.
 */
export function groupByClass(rows: readonly StudentRow[]): StudentGroup[] {
  const groups: StudentGroup[] = []
  const byId = new Map<string, StudentGroup>()

  for (const row of rows) {
    const existing = byId.get(row.classId)
    if (existing) {
      existing.rows.push(row)
      continue
    }
    const group: StudentGroup = { classId: row.classId, heading: classHeading(row), rows: [row] }
    byId.set(row.classId, group)
    groups.push(group)
  }

  return groups
}

/**
 * What a class is called on this screen.
 *
 * ⚠️ THE CLASS NAME IS THE FALLBACK, NOT THE OTHER WAY ROUND. `grade` and
 * `section` are the columns a school's own register is keyed on; `name` is a
 * free label that is often the same two words joined. When both parts exist the
 * heading is "Nursery · A", and when neither does the name is all there is.
 */
export function classHeading(row: StudentRow): string {
  const parts = [row.grade, row.section].filter((p): p is string => p !== null && p !== '')
  return parts.length > 0 ? parts.join(' · ') : row.className
}

export interface ClassOption {
  classId: string
  label: string
  count: number
}

/**
 * The classes in the filter, derived from the rows rather than fetched.
 *
 * 🔴 NO SECOND REQUEST. `GET /classes` would answer the same question and add a
 * round trip, a second loading state and a way for the two lists to disagree —
 * a class in the dropdown with no child behind it, which reads as a filter that
 * is broken rather than a class that is empty. Every class with a child in it is
 * already on this screen.
 */
export function classOptions(rows: readonly StudentRow[]): ClassOption[] {
  return groupByClass(rows).map((g) => ({
    classId: g.classId,
    label: g.heading,
    count: g.rows.length,
  }))
}

/**
 * The class filter the control and the table can BOTH honour.
 *
 * 🔴 A `<select>` CAN ONLY DISPLAY AN OPTION IT HAS, AND THIS LIST LOSES ONES.
 * `classOptions` is derived from the children on screen, so a class leaves the
 * dropdown the moment its last child does — while `classId` is free-floating
 * state that survives the reload. The browser then falls back to painting the
 * first option, "All Classes", and the filter goes on hiding every other class:
 * a control stating the opposite of what the table beneath it is doing, with no
 * way for an admin to clear a filter the screen denies having.
 *
 * ⚠️ ONE EXPRESSION FEEDS BOTH, WHICH IS THE WHOLE POINT. Resolving the
 * control's value and `filterStudents`'s argument separately is how they came
 * apart; a caller that reads this once and passes it to each cannot drift.
 *
 * ⚠️ AN EMPTY LIST MEANS "NOT LOADED YET", NOT "CLASS GONE" — and both answer
 * null here, which is correct for each. There are no rows to filter mid-load,
 * and the table is drawing a skeleton over the top of them.
 */
export function resolveClassFilter(
  classes: readonly ClassOption[],
  classId: string | null,
): string | null {
  if (classId === null) return null
  return classes.some((c) => c.classId === classId) ? classId : null
}

/* ───────────────────────────────────────────────────────── the headline ── */

export interface StudentTotals {
  children: number
  classes: number
  /** 🔴 A count of children who have never played, never a percentage. */
  neverPlayed: number
  /** Children with no parent contact at all — nobody to send a report to. */
  noParent: number
}

/**
 * 🔴 WHAT IS DELIBERATELY ABSENT: A "PLAYED THIS WEEK" FIGURE.
 *
 * E1 already draws one, from the server's session fold, one nav item away. A
 * second school-wide engagement number computed in the browser from a different
 * column is exactly the contradiction `design-system.md` §8 forbids — and the
 * head teacher who spotted the two disagreeing would be right to stop trusting
 * both. `neverPlayed` has no sibling anywhere in the product and is the one an
 * admin can act on this morning.
 */
export function summariseStudents(rows: readonly StudentRow[], now: Date): StudentTotals {
  let neverPlayed = 0
  let noParent = 0
  const classes = new Set<string>()

  for (const row of rows) {
    classes.add(row.classId)
    if (activityState(row, now) === 'NEVER') neverPlayed += 1
    if (row.parentName === null && row.parentPhone === null) noParent += 1
  }

  return { children: rows.length, classes: classes.size, neverPlayed, noParent }
}

/* ────────────────────────────────────────────────────────────────── CSV ── */

/**
 * The list as a spreadsheet.
 *
 * 🔴 THE FOURTH COPY OF THE ESCAPER, AND IT IS THE SAME ONE. `coverageToCsv`
 * explains why the browser cannot import the backend's `csv-cell.ts`: the two
 * packages share generated *types*, not runtime code. **That file is the source
 * of truth; change one, change both.** The leading-character guard is what stops
 * a child called `=cmd` from being a formula when the file is opened in Excel.
 *
 * ⚠️ THE PARENT'S PHONE GOES OUT MASKED, because that is how it arrived. The
 * full number never reaches this browser, and a column headed "Parent phone"
 * carrying a masked value is the honest thing to hand a school rather than a
 * column quietly dropped.
 */
export function studentsToCsv(rows: readonly StudentRow[]): string {
  const esc = (v: string | number): string => {
    const s = String(v)
    const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
  }

  const lines = [
    ['Class', 'Roll', 'Student', 'Band', 'Parent', 'Parent phone', 'Last played']
      .map(esc)
      .join(','),
  ]

  for (const r of rows) {
    lines.push(
      [
        classHeading(r),
        r.rollNumber ?? '',
        r.name,
        r.ageBand,
        r.parentName ?? '',
        r.parentPhone ?? '',
        r.lastPlayed ?? '',
      ]
        .map(esc)
        .join(','),
    )
  }

  return lines.join('\r\n')
}

/* ─────────────────────────────────────────── E7 · adding one child ── */

/**
 * The classes a child may be enrolled into, in the order they should be read.
 *
 * ⚠️ ARCHIVED CLASSES ARE EXCLUDED, AND THE SERVER REFUSES THEM ANYWAY —
 * `StudentService.addStudent`: *"those children have left; a new child added to
 * last year's section would be invisible to every scoped query the school runs
 * and would surface a year later in a rollover."* Offering one would be a choice
 * whose only outcome is a refusal.
 */
export function enrollableClasses(classes: readonly ClassSummary[]): ClassSummary[] {
  return [...classes]
    .filter((c) => c.archivedAt === null)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
}

/** A child already on this class's register under this roll number. */
export interface RollClash {
  student: StudentRow
  /**
   * 🔴 WHETHER THE SERVER WILL CERTAINLY REFUSE, WHICH IS NOT THE SAME QUESTION
   * AS "DO THESE LOOK LIKE THE SAME ROLL".
   *
   * `StudentService.addStudent` normalises what ARRIVES — `normaliseRoll` turns
   * "01" into "1" — and then compares it against `rollNumber` **as stored**. A
   * register seeded or imported before that normalisation holds literal "01", so
   * a typed "1" does NOT collide there and the add succeeds. Blocking it would
   * be this form refusing something the server allows, which is the one thing a
   * client-side copy of a rule must never do.
   */
  certain: boolean
}

/**
 * Who already holds this roll number in this class, if anybody does.
 *
 * 🔴 A DELIBERATE SECOND COPY OF THE SERVER'S UNIQUENESS RULE, and the same
 * kind of copy `MIN_PASSWORD_LENGTH` is: it exists so somebody is told while the
 * box is under their cursor rather than after a round trip. The server still
 * refuses it — `@@unique([classId, rollNumber])` is the hinge of the product —
 * and this only decides what an admin is told first.
 *
 * ⚠️ IT NAMES THE CHILD, WHICH IS THE WHOLE VALUE. "That roll number is taken"
 * sends an admin down a register hunting; "roll 03 is Meera Nair" is the answer.
 * The roster is already loaded on this screen, so this costs no request.
 *
 * ⚠️ TWO STRENGTHS, NOT ONE — see `RollClash.certain`. An identical stored value
 * is a refusal waiting to happen; a merely equivalent one ("1" against a stored
 * "01") is a warning, because the server would take it.
 */
export function rollNumberClash(
  rows: readonly StudentRow[],
  classId: string,
  rollNumber: string,
): RollClash | null {
  const typed = rollNumber.trim()
  if (typed === '') return null
  const wanted = normaliseRollForCompare(typed)
  if (wanted === null) return null

  const here = rows.filter((r) => r.classId === classId && (r.rollNumber ?? '') !== '')

  /* Exact first: that is the one the server compares and certainly refuses. */
  const exact = here.find((r) => (r.rollNumber ?? '').trim() === typed)
  if (exact) return { student: exact, certain: true }

  const equivalent = here.find(
    (r) => normaliseRollForCompare(r.rollNumber ?? '') === wanted,
  )
  return equivalent ? { student: equivalent, certain: false } : null
}

/**
 * ⚠️ MIRRORS `normaliseRoll` ON THE SERVER: trim, then drop leading zeros so
 * "03" and "3" are one roll. A number that is not digits at all is compared
 * verbatim — the server keeps those as typed too.
 */
function normaliseRollForCompare(raw: string): string | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  return /^\d+$/.test(trimmed) ? String(Number(trimmed)) : trimmed.toLowerCase()
}

/**
 * Is this form complete enough to send?
 *
 * ⚠️ THE PARENT FIELDS ARE OPTIONAL AND THE PHONE IS THE ONE THAT COUNTS. A
 * parent NAME with no number creates nothing — `addStudent` keys the whole
 * parent half off `parentPhone` — so a form carrying only a name would silently
 * drop it, which is worse than refusing.
 */
export function canAddStudent(input: {
  classId: string
  name: string
  parentName: string
  parentPhone: string
}): boolean {
  if (input.classId === '') return false
  if (input.name.trim() === '') return false
  if (input.parentName.trim() !== '' && input.parentPhone.trim() === '') return false
  return true
}
